/**
 * Parses the free-form "action" string the LLM emits for each pick into
 * structured parts. The LLM is asked to format actions like:
 *
 *   "Entry: buy above $44.60; Stop loss: below $43.00;
 *    Take profit: $46.50 (+4.3%)"
 *
 * but in practice may omit some sections. Returns null if neither entry
 * nor stop could be parsed.
 *
 * Targets (T1/T2/T3) are *not* LLM-derived — they are auto-computed at
 * +1% / +1.5% / +2% from entry as a manual scale-out reference. The LLM
 * gives a single takeProfitPrice that we use for R:R calculation.
 *
 * Previously lived inline in AiAnalysisPanel.js. Extracted so the Risk
 * Assessment utils can also use it without circular imports.
 */

/** Find the first dollar amount in a string. Tolerates "$" / "~$" / ranges. */
export const extractPrice = (text) => {
  if (!text) return null;
  const match = text.match(/\$?([\d,]+\.?\d*)/);
  if (match) {
    return parseFloat(match[1].replace(',', ''));
  }
  return null;
};

/**
 * Manual exit ladder T1/T2/T3 = entry × 1.01 / 1.015 / 1.02. These are
 * scale-out reference points, NOT the LLM's take-profit target.
 */
export const calculateTargets = (entryPrice) => {
  if (!entryPrice || isNaN(entryPrice)) return null;
  return {
    t1: { price: entryPrice * 1.01, percent: 1 },
    t2: { price: entryPrice * 1.015, percent: 1.5 },
    t3: { price: entryPrice * 1.02, percent: 2 }
  };
};

/**
 * Parse a free-form action string into {entry, stopLoss, takeProfit,
 * entryPrice, takeProfitPrice, targets, raw}. Returns null if neither
 * entry nor stop could be parsed.
 */
export const parseAction = (action) => {
  if (!action) return null;

  const parts = {
    entry: null,
    stopLoss: null,
    takeProfit: null,
    takeProfitPrice: null,
    entryPrice: null,
    targets: null,
    raw: action
  };

  const entryMatch = action.match(/Entry:?\s*([^;]+)/i);
  const stopMatch = action.match(/Stop\s*loss:?\s*([^;]+)/i);

  if (entryMatch) {
    parts.entry = entryMatch[1].trim();
    parts.entryPrice = extractPrice(parts.entry);
    if (parts.entryPrice) {
      parts.targets = calculateTargets(parts.entryPrice);
    }
  }
  if (stopMatch) parts.stopLoss = stopMatch[1].trim();

  const takeProfitMatch = action.match(/Take\s*profit:?\s*([^;]+)/i);
  if (takeProfitMatch) {
    parts.takeProfit = takeProfitMatch[1].trim();
    parts.takeProfitPrice = extractPrice(parts.takeProfit);
  }

  return (parts.entry || parts.stopLoss) ? parts : null;
};
